import { Injectable, inject, DestroyRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import {
  ApplicationInsights,
  IExceptionTelemetry,
  IEventTelemetry,
  IMetricTelemetry,
  ITraceTelemetry,
  IPageViewTelemetry,
  SeverityLevel,
} from '@microsoft/applicationinsights-web';
import { environment } from '@environment';

export { SeverityLevel };

export type TelemetryProperties = Record<
  string,
  string | number | boolean | undefined
>;

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly config = environment.applicationInsights;

  initialize(): void {
    if (!this.config?.enabled || !this.config?.connectionString) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: this.config.connectionString,
        enableAutoRouteTracking: false,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        autoTrackPageVisitTime: true,
        enableUnhandledPromiseRejectionTracking: true,
      },
    });

    this.appInsights.loadAppInsights();
    this.setupRouterTracking();
  }

  private setupRouterTracking(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.trackPageView({
          name: event.urlAfterRedirects,
          uri: event.urlAfterRedirects,
        });
      });
  }

  setAuthenticatedUser(userId: string, accountId?: string): void {
    if (!this.appInsights) return;
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearAuthenticatedUser(): void {
    if (!this.appInsights) return;
    this.appInsights.clearAuthenticatedUserContext();
  }

  trackPageView(pageView?: IPageViewTelemetry): void {
    if (!this.appInsights) return;
    this.appInsights.trackPageView(pageView);
  }

  trackEvent(event: IEventTelemetry, properties?: TelemetryProperties): void {
    if (!this.appInsights) return;
    this.appInsights.trackEvent(event, properties);
  }

  trackException(
    exception: Error,
    properties?: TelemetryProperties,
    severityLevel?: SeverityLevel
  ): void {
    if (!this.appInsights) return;

    const telemetry: IExceptionTelemetry = {
      exception,
      severityLevel: severityLevel ?? SeverityLevel.Error,
      properties: properties as Record<string, string>,
    };

    this.appInsights.trackException(telemetry);
  }

  trackMetric(metric: IMetricTelemetry): void {
    if (!this.appInsights) return;
    this.appInsights.trackMetric(metric);
  }

  trackTrace(trace: ITraceTelemetry, properties?: TelemetryProperties): void {
    if (!this.appInsights) return;
    this.appInsights.trackTrace({
      ...trace,
      properties: properties as Record<string, string>,
    });
  }

  trackHttpRequest(
    url: string,
    method: string,
    duration: number,
    success: boolean,
    statusCode: number,
    properties?: TelemetryProperties
  ): void {
    if (!this.appInsights) return;

    this.appInsights.trackDependencyData({
      id: crypto.randomUUID(),
      name: `${method} ${url}`,
      duration,
      success,
      responseCode: statusCode,
      type: 'Http',
      target: new URL(url, window.location.origin).host,
      properties: properties as Record<string, string>,
    });
  }

  flush(): void {
    if (!this.appInsights) return;
    this.appInsights.flush();
  }
}
